import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { authFetch } from '@/lib/api';

/**
 * High-performance Canvas 2D Point Cloud Viewer.
 * Uses putImageData for million-point rendering. Orbit camera via mouse drag/scroll.
 *
 * Imperative API (via ref):
 *   - setOrbit({ rx, ry, z })         — direct orbit setter
 *   - getOrbit()                       — current orbit state
 *   - getNormalization()               — { center, scale }, used to map screen → world
 *
 * Callbacks:
 *   - onPick(world)                    — fires on a non-drag click with world XYZ
 *   - onOrbitChange({rx, ry, z})       — fires after the user manipulates the camera
 */
const PointCloudViewer = forwardRef(function PointCloudViewer({
  apiUrl, pointSize = 3, colorMode = 'rgb', height = 340, isDark = true,
  onPick, onOrbitChange,
}, ref) {
  const canvasRef = useRef(null);
  const dataRef = useRef(null);
  // Stores the original world-space coords + the centering/scale used to normalize them.
  // Needed so click-to-pick can return real world XYZ.
  const origRef = useRef({ p: null, center: { x: 0, y: 0, z: 0 }, scale: 1 });
  const camRef = useRef({ rx: -0.6, ry: 0.4, z: 1.2 });
  // Track viewport for screen↔world math
  const viewportRef = useRef({ W: 0, H: 0 });
  const dragRef = useRef({ active: false, px: 0, py: 0, moved: false });
  const frameRef = useRef(null);
  const [pointCount, setPointCount] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load data
  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const isBinary = apiUrl.includes('points-bin');

    authFetch(apiUrl)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return isBinary ? r.arrayBuffer() : r.json(); })
      .then(result => {
        if (cancelled) return;
        let np, nc, count, totalInFile;

        if (isBinary) {
          const view = new DataView(result);
          count = view.getUint32(0, true);
          totalInFile = count;
          np = new Float32Array(result, 4, count * 3);
          nc = new Uint8Array(result, 4 + count * 12, count * 3);

          // Normalize positions to [-1,1]
          let mnx = Infinity, mxx = -Infinity, mny = Infinity, mxy = -Infinity, mnz = Infinity, mxz = -Infinity;
          for (let i = 0; i < count; i++) {
            const x = np[i*3], y = np[i*3+1], z = np[i*3+2];
            if (x < mnx) mnx = x; if (x > mxx) mxx = x;
            if (y < mny) mny = y; if (y > mxy) mxy = y;
            if (z < mnz) mnz = z; if (z > mxz) mxz = z;
          }
          const cx = (mnx+mxx)/2, cy = (mny+mxy)/2, cz = (mnz+mxz)/2;
          const md = Math.max(mxx-mnx, mxy-mny, mxz-mnz) || 1;
          const sc = 2/md;
          const norm = new Float32Array(count * 3);
          for (let i = 0; i < count * 3; i += 3) {
            norm[i] = (np[i]-cx)*sc;
            norm[i+1] = (np[i+1]-cy)*sc;
            norm[i+2] = (np[i+2]-cz)*sc;
          }
          np = norm;
          // Cache original world coords + normalization so click-to-pick can return world XYZ
          origRef.current = {
            p: new Float32Array(result, 4, count * 3),
            center: { x: cx, y: cy, z: cz },
            scale: sc,
          };
        } else {
          const data = result;
          count = data.count || 0;
          totalInFile = data.totalInFile || count;
          if (!count) { dataRef.current = null; setPointCount(0); setLoading(false); return; }
          const p = data.positions;
          const c = data.colors;
          let mnx = Infinity, mxx = -Infinity, mny = Infinity, mxy = -Infinity, mnz = Infinity, mxz = -Infinity;
          for (let i = 0; i < count; i++) {
            const x = p[i*3], y = p[i*3+1], z = p[i*3+2];
            if (x < mnx) mnx = x; if (x > mxx) mxx = x;
            if (y < mny) mny = y; if (y > mxy) mxy = y;
            if (z < mnz) mnz = z; if (z > mxz) mxz = z;
          }
          const cx = (mnx+mxx)/2, cy = (mny+mxy)/2, cz = (mnz+mxz)/2;
          const md = Math.max(mxx-mnx, mxy-mny, mxz-mnz) || 1;
          const sc = 2/md;
          np = new Float32Array(count*3);
          nc = new Uint8Array(count*3);
          const origP = new Float32Array(count * 3);
          for (let i = 0; i < count; i++) {
            origP[i*3]   = p[i*3];
            origP[i*3+1] = p[i*3+1];
            origP[i*3+2] = p[i*3+2];
            np[i*3]   = (p[i*3]-cx)*sc;
            np[i*3+1] = (p[i*3+1]-cy)*sc;
            np[i*3+2] = (p[i*3+2]-cz)*sc;
            nc[i*3]   = Math.min(255, Math.max(0, Math.round((c[i*3] || 0.5) * 255)));
            nc[i*3+1] = Math.min(255, Math.max(0, Math.round((c[i*3+1] || 0.5) * 255)));
            nc[i*3+2] = Math.min(255, Math.max(0, Math.round((c[i*3+2] || 0.5) * 255)));
          }
          origRef.current = { p: origP, center: { x: cx, y: cy, z: cz }, scale: sc };
        }

        if (!count) { dataRef.current = null; setPointCount(0); setLoading(false); return; }
        dataRef.current = { p: np, c: nc, n: count };
        setPointCount(count);
        setTotalPoints(totalInFile);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [apiUrl]);

  // Render loop using putImageData for performance
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      if (W === 0 || H === 0) { raf = requestAnimationFrame(draw); return; }
      viewportRef.current.W = W;
      viewportRef.current.H = H;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const cam = camRef.current;

      // Create image data for pixel-level drawing
      const imgData = ctx.createImageData(W, H);
      const buf = imgData.data;

      // Fill background
      const bgR = isDark ? 6 : 240, bgG = isDark ? 10 : 244, bgB = isDark ? 20 : 248;
      for (let i = 0; i < buf.length; i += 4) {
        buf[i] = bgR; buf[i+1] = bgG; buf[i+2] = bgB; buf[i+3] = 255;
      }

      // Depth buffer for proper occlusion
      const depthBuf = new Float32Array(W * H).fill(Infinity);

      // Projection constants
      const cosRX = Math.cos(cam.rx), sinRX = Math.sin(cam.rx);
      const cosRY = Math.cos(cam.ry), sinRY = Math.sin(cam.ry);
      const dist = 3;
      const scaleX = cam.z * W * 0.35;
      const scaleY = cam.z * H * 0.35;
      const halfW = W / 2;
      const halfH = H / 2;
      const ps = Math.max(1, Math.round(pointSize * 0.5));

      // Draw grid lines on the image buffer (ground plane y=0)
      const gridColor = isDark ? [30, 50, 80] : [190, 195, 210];
      for (let gi = -5; gi <= 5; gi++) {
        const g = gi * 0.2;
        for (let t = -100; t <= 100; t++) {
          const tt = t * 0.01;
          // Line along Z at x=g, y=0
          const rx1 = g * cosRX - tt * sinRX;
          const rz1 = g * sinRX + tt * cosRX;
          const ry1 = -rz1 * sinRY;
          const rz2 = rz1 * cosRY;
          const s1 = dist / (dist + rz2);
          const sx1 = Math.round(halfW + rx1 * s1 * scaleX);
          const sy1 = Math.round(halfH - ry1 * s1 * scaleY);
          if (sx1 >= 0 && sx1 < W && sy1 >= 0 && sy1 < H) {
            const idx = (sy1 * W + sx1) * 4;
            buf[idx] = gridColor[0]; buf[idx+1] = gridColor[1]; buf[idx+2] = gridColor[2];
          }
          // Line along X at z=g, y=0
          const rx3 = tt * cosRX - g * sinRX;
          const rz3 = tt * sinRX + g * cosRX;
          const ry3 = -rz3 * sinRY;
          const rz4 = rz3 * cosRY;
          const s3 = dist / (dist + rz4);
          const sx3 = Math.round(halfW + rx3 * s3 * scaleX);
          const sy3 = Math.round(halfH - ry3 * s3 * scaleY);
          if (sx3 >= 0 && sx3 < W && sy3 >= 0 && sy3 < H) {
            const idx = (sy3 * W + sx3) * 4;
            buf[idx] = gridColor[0]; buf[idx+1] = gridColor[1]; buf[idx+2] = gridColor[2];
          }
        }
      }

      // Draw axes
      const axesColors = [[220,60,60],[60,180,60],[60,100,220]];
      const axesDirs = [[1,0,0],[0,1,0],[0,0,1]];
      for (let a = 0; a < 3; a++) {
        for (let t = 0; t <= 50; t++) {
          const f = t * 0.006;
          const ax = axesDirs[a][0]*f, ay = axesDirs[a][1]*f, az = axesDirs[a][2]*f;
          const rx = ax * cosRX - az * sinRX;
          const rz = ax * sinRX + az * cosRX;
          const ry2 = ay * cosRY - rz * sinRY;
          const rz2 = ay * sinRY + rz * cosRY;
          const s = dist / (dist + rz2);
          const sx = Math.round(halfW + rx * s * scaleX);
          const sy = Math.round(halfH - ry2 * s * scaleY);
          if (sx >= 0 && sx < W && sy >= 0 && sy < H) {
            for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
              const px = sx+dx, py = sy+dy;
              if (px >= 0 && px < W && py >= 0 && py < H) {
                const idx = (py * W + px) * 4;
                buf[idx] = axesColors[a][0]; buf[idx+1] = axesColors[a][1]; buf[idx+2] = axesColors[a][2];
              }
            }
          }
        }
      }

      // Draw point cloud using pixel buffer (fastest possible)
      const data = dataRef.current;
      if (data) {
        const { p, c, n } = data;
        for (let i = 0; i < n; i++) {
          const px = p[i*3], py = p[i*3+1], pz = p[i*3+2];

          // Rotate Y (orbit horizontal)
          const rx = px * cosRX - pz * sinRX;
          const rz = px * sinRX + pz * cosRX;
          // Rotate X (orbit vertical)
          const ry = py * cosRY - rz * sinRY;
          const rz2 = py * sinRY + rz * cosRY;

          // Perspective
          const denom = dist + rz2;
          if (denom <= 0.01) continue;
          const s = dist / denom;
          const sx = halfW + rx * s * scaleX;
          const sy = halfH - ry * s * scaleY;

          const isx = Math.round(sx);
          const isy = Math.round(sy);
          if (isx < 0 || isx >= W || isy < 0 || isy >= H) continue;

          // Depth test
          const di = isy * W + isx;
          if (rz2 >= depthBuf[di]) continue;

          const cr = c[i*3], cg = c[i*3+1], cb = c[i*3+2];

          // Draw point (ps x ps pixels)
          if (ps <= 1) {
            depthBuf[di] = rz2;
            const idx = di * 4;
            buf[idx] = cr; buf[idx+1] = cg; buf[idx+2] = cb;
          } else {
            const half = Math.floor(ps / 2);
            for (let dy = -half; dy <= half; dy++) {
              const py2 = isy + dy;
              if (py2 < 0 || py2 >= H) continue;
              for (let dx = -half; dx <= half; dx++) {
                const px2 = isx + dx;
                if (px2 < 0 || px2 >= W) continue;
                const pi = py2 * W + px2;
                if (rz2 < depthBuf[pi]) {
                  depthBuf[pi] = rz2;
                  const idx = pi * 4;
                  buf[idx] = cr; buf[idx+1] = cg; buf[idx+2] = cb;
                }
              }
            }
          }
        }
      }

      ctx.putImageData(imgData, 0, 0);
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [isDark, pointSize]);

  // Mouse controls
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const md = (e) => { dragRef.current = { active: true, px: e.clientX, py: e.clientY, downX: e.clientX, downY: e.clientY, moved: false }; };
    const mu = () => { dragRef.current.active = false; };
    const mm = (e) => {
      if (!dragRef.current.active) return;
      const dx = e.clientX - dragRef.current.px;
      const dy = e.clientY - dragRef.current.py;
      if (Math.abs(e.clientX - (dragRef.current.downX || e.clientX)) > 3 ||
          Math.abs(e.clientY - (dragRef.current.downY || e.clientY)) > 3) {
        dragRef.current.moved = true;
      }
      camRef.current.rx += dx * 0.008;
      camRef.current.ry = Math.max(-1.4, Math.min(1.4, camRef.current.ry - dy * 0.008));
      dragRef.current.px = e.clientX;
      dragRef.current.py = e.clientY;
      if (onOrbitChange) onOrbitChange({ ...camRef.current });
    };
    const mw = (e) => {
      e.preventDefault();
      camRef.current.z = Math.max(0.3, Math.min(8, camRef.current.z * (1 - e.deltaY * 0.001)));
      if (onOrbitChange) onOrbitChange({ ...camRef.current });
    };
    const click = (e) => {
      // Only treat as pick when this was a non-drag tap and a callback is registered
      if (!onPick || dragRef.current.moved) return;
      const data = dataRef.current;
      const orig = origRef.current;
      if (!data || !orig.p) return;
      const rect = el.getBoundingClientRect();
      const pr = (canvasRef.current && canvasRef.current.width / rect.width) || 1;
      const cx = (e.clientX - rect.left) * pr;
      const cy = (e.clientY - rect.top) * pr;
      // Find nearest projected point — reuse same projection math as the render loop
      const cam = camRef.current;
      const { W, H } = viewportRef.current;
      const cosRX = Math.cos(cam.rx), sinRX = Math.sin(cam.rx);
      const cosRY = Math.cos(cam.ry), sinRY = Math.sin(cam.ry);
      const dist = 3;
      const scaleX = cam.z * W * 0.35;
      const scaleY = cam.z * H * 0.35;
      const halfW = W / 2, halfH = H / 2;
      const { p, n } = data;
      let bestI = -1, bestD2 = Infinity, bestZ = Infinity;
      for (let i = 0; i < n; i++) {
        const px = p[i*3], py = p[i*3+1], pz = p[i*3+2];
        const rx = px * cosRX - pz * sinRX;
        const rz = px * sinRX + pz * cosRX;
        const ry = py * cosRY - rz * sinRY;
        const rz2 = py * sinRY + rz * cosRY;
        const denom = dist + rz2;
        if (denom <= 0.01) continue;
        const s = dist / denom;
        const sx = halfW + rx * s * scaleX;
        const sy = halfH - ry * s * scaleY;
        const d2 = (sx - cx) * (sx - cx) + (sy - cy) * (sy - cy);
        // Pick closest in screen-space, tie-broken by depth
        if (d2 < bestD2 || (d2 < bestD2 + 4 && rz2 < bestZ)) {
          bestD2 = d2; bestZ = rz2; bestI = i;
        }
      }
      const tolPx = 20 * pr;
      if (bestI < 0 || bestD2 > tolPx * tolPx) return;
      // Return WORLD-space XYZ from cached original positions
      const op = orig.p;
      onPick({ x: op[bestI*3], y: op[bestI*3+1], z: op[bestI*3+2], index: bestI });
    };
    el.addEventListener('mousedown', md);
    window.addEventListener('mouseup', mu);
    window.addEventListener('mousemove', mm);
    el.addEventListener('wheel', mw, { passive: false });
    el.addEventListener('click', click);
    return () => {
      el.removeEventListener('mousedown', md);
      window.removeEventListener('mouseup', mu);
      window.removeEventListener('mousemove', mm);
      el.removeEventListener('wheel', mw);
      el.removeEventListener('click', click);
    };
  }, [onPick, onOrbitChange]);

  // Imperative camera API for parent components (camera sync, jump-to-view, etc.)
  useImperativeHandle(ref, () => ({
    setOrbit: ({ rx, ry, z }) => {
      if (typeof rx === 'number') camRef.current.rx = rx;
      if (typeof ry === 'number') camRef.current.ry = Math.max(-1.4, Math.min(1.4, ry));
      if (typeof z === 'number') camRef.current.z = Math.max(0.3, Math.min(8, z));
    },
    getOrbit: () => ({ ...camRef.current }),
    getNormalization: () => ({ ...origRef.current }),
  }), []);

  // Canvas sizing
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const pr = window.devicePixelRatio || 1;
    const setSize = () => {
      const w = c.parentElement ? c.parentElement.clientWidth : 600;
      c.width = Math.round(w * pr);
      c.height = Math.round(height * pr);
      c.style.width = w + 'px';
      c.style.height = height + 'px';
    };
    setSize();
    const ro = new ResizeObserver(setSize);
    if (c.parentElement) ro.observe(c.parentElement);
    return () => ro.disconnect();
  }, [height]);

  const fmtK = (n) => n >= 1000000 ? (n/1000000).toFixed(1) + 'M' : n >= 1000 ? (n/1000).toFixed(0) + 'K' : n;

  return (
    <div className="relative" style={{ height }}>
      <div style={{ width: '100%', height }}>
        <canvas ref={canvasRef} style={{ cursor: 'grab', borderRadius: 8, display: 'block' }} data-testid="point-cloud-canvas" />
      </div>
      <div className="absolute top-2 left-2 flex items-center gap-2">
        {loading && <span className="text-[9px] px-2 py-0.5 rounded bg-black/60 text-cyan-400 animate-pulse">Loading...</span>}
        {pointCount > 0 && !loading && (
          <span className="text-[9px] px-2 py-0.5 rounded bg-black/60 text-white">
            {fmtK(pointCount)} points{totalPoints > pointCount ? ` / ${fmtK(totalPoints)} total` : ''}
          </span>
        )}
        {error && <span className="text-[9px] px-2 py-0.5 rounded bg-red-900/60 text-red-300">{error}</span>}
      </div>
      <div className="absolute bottom-2 right-2 text-[8px] px-2 py-0.5 rounded bg-black/40 text-slate-400">
        Drag to orbit | Scroll to zoom{onPick ? ' | Click to pick' : ''}
      </div>
    </div>
  );
});

export default PointCloudViewer;
